import { InteractionRedirect, InteractionState } from '@tabel/shared/schemas/oauth';
import { ApiError } from '../../lib/errors';

/*
 * JSON-адреса страницы входа и согласия ИИ-агента (005, research R15; specs/005-rules-mcp/contracts/oauth.md,
 * «JSON-адреса страницы входа и согласия»). Их отдаёт модуль `oauth` сервера, сессия табеля для них не нужна:
 * заголовков роли и объекта нет, на 401 страница входа табеля не открывается. Ошибки — тело 004
 * `{ error: { code, message, requestId } }`.
 */

export type { InteractionScope, InteractionState } from '@tabel/shared/schemas/oauth';

const base = (uid: string) => `/oauth/interaction/${encodeURIComponent(uid)}`;

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const isText = (v: unknown): v is string => typeof v === 'string';

/** Ответ не по договору (например, HTML вместо JSON) — сбой сервера с кодом ошибки. */
const badAnswer = (status: number, requestId: string | null) =>
  new ApiError(status, 'INTERNAL', 'Ошибка сервера', requestId);

async function call(method: 'GET' | 'POST', url: string, body?: unknown): Promise<unknown> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: 'same-origin',
      // Согласие и отказ — с пустым телом и без `Content-Type`: пустое тело JSON сервер отклоняет.
      ...(body !== undefined
        ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
        : {}),
    });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Нет связи с сервером', null);
  }
  const requestId = res.headers.get('x-request-id');
  const data: unknown = await res.json().catch(() => undefined);
  if (!res.ok) {
    const e = isObject(data) && isObject(data.error) ? data.error : {};
    const details = isObject(e.details) ? e.details : undefined;
    throw new ApiError(
      res.status,
      isText(e.code) ? (e.code as ApiError['code']) : res.status === 404 ? 'NOT_FOUND' : 'INTERNAL',
      isText(e.message) ? e.message : `Ошибка ${res.status}`,
      isText(e.requestId) ? e.requestId : requestId,
      typeof details?.retryAfterSec === 'number' ? details.retryAfterSec : undefined, // `LOGIN_LOCKED`
      details,
    );
  }
  if (data === undefined) throw badAnswer(res.status, requestId);
  return data;
}

/** Шаг взаимодействия и что покажет страница. 404 — взаимодействия нет или оно истекло. */
export async function interactionState(uid: string): Promise<InteractionState> {
  // Ответ по договору: иначе страница показала бы пустые поля.
  const parsed = InteractionState.safeParse(await call('GET', `${base(uid)}/state`));
  if (!parsed.success) throw badAnswer(200, null);
  return parsed.data;
}

/** Вход логином и паролем: проверка и блокировка — как у `/api/auth/login`. */
export async function interactionLogin(uid: string, login: string, password: string): Promise<void> {
  await call('POST', `${base(uid)}/login`, { login, password });
}

async function decide(uid: string, what: 'consent' | 'abort'): Promise<string> {
  const parsed = InteractionRedirect.safeParse(await call('POST', `${base(uid)}/${what}`));
  if (!parsed.success) throw badAnswer(200, null);
  return parsed.data.redirectTo;
}

/** «Разрешить»: адрес, куда браузер уходит с кодом входа. */
export const interactionConsent = (uid: string) => decide(uid, 'consent');

/** «Отказать»: адрес возврата с `error=access_denied`. */
export const interactionAbort = (uid: string) => decide(uid, 'abort');

/** Взаимодействие истекло или его нет: 404 на любом адресе страницы. */
export const isExpired = (err: unknown): boolean => err instanceof ApiError && err.status === 404;
